import { useCompanySettingsPayrollUpdate } from '../../../../../../queries/company/mutations';
import { Autocomplete, TextField, SvgIcon } from '@mui/material';
import { PayrollSystemEnum } from '@pxtra-client-lib-legacy/gen';
import { useState, useEffect } from 'react';
import { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { PayrollExportMappingRow } from '.';
import { useTranslation } from 'react-i18next';
import ArrowDown from '../../../../../../lib/icons/svg/arrow_down.svg?react';

export const ExternalIDCell: ColumnDefTemplate<
  CellContext<PayrollExportMappingRow, string> & {
    company: string;
    isCellEditable: boolean;
    options: { text: string; value: string }[];
    system?: PayrollSystemEnum;
    onUpdateRow?: ({ lohnart, val }: { lohnart: string; val: string }) => void;
  }
> = ({ getValue, row, isCellEditable, options, system, onUpdateRow }) => {
  const { t } = useTranslation(['common']);
  const initialValue = getValue();
  const initialOption = options.find((o) => o.value === initialValue);
  const [value, setValue] = useState<{ text: string; value: string } | undefined>(initialOption);
  const [textValue, setTextValue] = useState<string>(initialValue || '');

  const { status } = useCompanySettingsPayrollUpdate();

  useEffect(() => {
    const newInitialOption = options.find((o) => o.value === initialValue);
    setValue(newInitialOption);
  }, [options, initialValue]);

  const handleChange = (newValue: { text: string; value: string } | null) => {
    setValue(newValue || undefined);
    if (system === 'PERSONIO') {
      onUpdateRow?.({ lohnart: row.original.lohnart, val: newValue?.value || '' });
    }
  };

  if (!isCellEditable) {
    return initialOption?.text ?? initialValue;
  }

  const isPersonio = system === 'PERSONIO';

  if (isPersonio) {
    return (
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.text}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('common:SEARCH')}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F4F5F7',
                borderRadius: '0.75rem',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: 'transparent'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent'
                }
              }
            }}
          />
        )}
        popupIcon={<SvgIcon component={ArrowDown} color="primary" />}
        value={value}
        onChange={(_, newValue) => handleChange(newValue)}
        disabled={status === 'pending'}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
      />
    );
  }

  return (
    <TextField
      fullWidth
      variant="outlined"
      value={textValue}
      onChange={(e) => {
        setTextValue(e.target.value);
      }}
      onKeyUp={() => {
        onUpdateRow?.({ lohnart: row.original.lohnart, val: textValue });
      }}
      onBlur={() => {
        onUpdateRow?.({ lohnart: row.original.lohnart, val: textValue });
      }}
      disabled={status === 'pending' || system === 'PAYCHEX_PLUS'}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#F4F5F7',
          borderRadius: '0.75rem',
          '& fieldset': {
            borderColor: 'transparent'
          },
          '&:hover fieldset': {
            borderColor: 'transparent'
          },
          '&.Mui-focused fieldset': {
            borderColor: 'transparent'
          }
        }
      }}
    />
  );
};
